import type { CSSProperties, ReactNode } from 'react';
import type { Device, IssueSeverity, LevelRange } from '@daevio/shared';
import { severityLabel } from '../lib/synthetic.ts';

/** Stroke icons of the app, 16 px by default, coloured by the text around them. */
const PATHS = {
  flask: 'M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-9V3',
  users: 'M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35M15.5 4.15a3.5 3.5 0 0 1 0 6.7',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1',
  plus: 'M12 5v14M5 12h14',
  chevronDown: 'm6 9 6 6 6-6',
  chevronLeft: 'm15 18-6-6 6-6',
  chevronRight: 'm9 18 6-6-6-6',
  updown: 'm7 15 5 5 5-5M7 9l5-5 5 5',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  stop: 'M7 7h10v10H7z',
  play: 'M8 5v14l11-7z',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8M12 12h.01',
  route: 'M6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4M18 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4M8 17h7a3 3 0 0 0 0-6H9a3 3 0 0 1 0-6h7',
  list: 'M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01',
  exit: 'M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H4',
  calendar: 'M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1M16 3v4M8 3v4M4 10h16',
  language: 'M4 5h8M8 3v2M6 5c0 4 3 7 6 8M10 5c0 4-3 7-6 8M13 21l4-9 4 9M14.5 18h5',
  check: 'm5 12 5 5 9-10',
  sparkle: 'M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.1 2.1M15.6 15.6l2.1 2.1M6.3 17.7l2.1-2.1M15.6 8.4l2.1-2.1',
  tap: 'M9 11V5a2 2 0 1 1 4 0v5M13 10V9a2 2 0 1 1 4 0v2M17 11a2 2 0 1 1 4 0v3a7 7 0 0 1-7 7h-1a7 7 0 0 1-5.6-2.8L4 14.5a2 2 0 0 1 3.2-2.4L9 14',
  arrowDown: 'M12 5v14M6 13l6 6 6-6',
  arrowUp: 'M12 19V5M6 11l6-6 6 6',
  type: 'M4 7V5h16v2M9 19h6M12 5v14',
  back: 'M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-1',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 7v5l3 2',
  flag: 'M5 21V4M5 4h11l-2 4 2 4H5',
  alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  copy: 'M9 9h10v10H9zM5 15V5h10',
  desktop: 'M4 5h16v11H4zM9 20h6M12 16v4',
  mobile: 'M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1M11 18h2',
  tablet: 'M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1M11 18h2',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  shield: 'M12 3 5 6v5c0 4.5 3 8.5 7 10 4-1.5 7-5.5 7-10V6z',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M20 20l-4-4',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6 6 18',
  home: 'M4 11 12 4l8 7M6 10v10h12V10',
  lock: 'M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2M9 7h7',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 11v5M12 8h.01',
  user: 'M18 20v-1.5a4 4 0 0 0-4-4h-4a4 4 0 0 0-4 4V20M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7',
  key: 'M8.5 18a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9M11.7 10.3 20 2M17 5l2.5 2.5M14.5 7.5 17 10',
  upload: 'M12 20V9M7 14l5-5 5 5M5 4h14',
  replay: 'M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2M10 9.5v5l4.5-2.5z',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 16, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

export const deviceIcon: Record<Device, IconName> = { desktop: 'desktop', tablet: 'tablet', mobile: 'mobile' };

/** "Impulse buyer from an ad" → "IB"; one word → its first two letters. */
export function initialsOf(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/[\s-]+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]!;
  // "Over 60 on a smartphone" → "60": a number names a persona better than its letters.
  const number = words.find((w) => /^\d+$/.test(w));
  if (number) return number.slice(0, 2);
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  return (first[0]! + words[1]![0]!).toUpperCase();
}

/** A visitor or persona as its orb, with the device in a small badge. The name stays for screen readers' neighbours, not here. */
export function Avatar({ index, device, size = 'md' }: { name?: string; index: number; device?: Device; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={`avatar avatar-${size}`} style={{ background: orbBackground(index) } as CSSProperties} aria-hidden="true">
      {device && (
        <span className="avatar-device">
          <Icon name={deviceIcon[device]} size={size === 'lg' ? 13 : size === 'sm' ? 9 : 11} />
        </span>
      )}
    </span>
  );
}

/** Overlapping avatars of the visitors of a test. */
export function AvatarStack({ people, max = 5 }: { people: { name: string; index: number }[]; max?: number }) {
  const shown = people.slice(0, max);
  return (
    <span className="avatar-stack">
      {shown.map((p, i) => (
        <Avatar key={i} index={p.index} size="sm" />
      ))}
      {people.length > max && <span className="avatar-more">+{people.length - max}</span>}
    </span>
  );
}

export type PillTone = 'blue' | 'green' | 'rose' | 'grey' | 'amber';

/** A state with its dot: Running, Completed, Left… */
export function Pill({ tone, children, testId }: { tone: PillTone; children: ReactNode; testId?: string }) {
  return (
    <span className={`pill pill-${tone}`} data-testid={testId}>
      <i aria-hidden="true" />
      {children}
    </span>
  );
}

export function Severity({ severity }: { severity: IssueSeverity }) {
  return <span className={`sev sev-${severity}`}>{severityLabel[severity]}</span>;
}

/** Five segments for a 1–5 trait; a range between visits shows its upper part lighter. */
export function TraitBar({ label, value }: { label: string; value: LevelRange }) {
  const ranged = value.min !== value.max;
  return (
    <span className="trait-bar">
      <span className="trait-bar-label mono">
        {label}
        {ranged ? ` ${value.min}-${value.max}` : ''}
      </span>
      <span className="trait-bar-segs" aria-label={`${label} ${ranged ? `${value.min} to ${value.max}` : value.min} of 5`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <i key={n} className={n <= value.min ? 'on' : n <= value.max ? 'maybe' : undefined} />
        ))}
      </span>
    </span>
  );
}

/** The mono breadcrumb above a title: "‹ Tests / vaultik.com". */
export function Crumbs({ children }: { children: ReactNode }) {
  return (
    <nav className="crumbs mono" aria-label="Breadcrumb">
      <Icon name="chevronLeft" size={12} />
      {children}
    </nav>
  );
}

/** The colours of a visitor's orb: Stripe's gradient stops, one blend per position in the test. */
const ORBS = [
  ['#FFF4E0', '#FFB36B', '#FF5E8A', '#A960EE'],
  ['#E9FBFF', '#90E0FF', '#635BFF', '#3B2E8F'],
  ['#E8FFF6', '#7FE3C4', '#1FA39A', '#0A4F6B'],
  ['#FFF7D6', '#FFCB57', '#FF7A59', '#E2365C'],
  ['#F3EDFF', '#C9B6FF', '#A960EE', '#635BFF'],
  ['#E6F0FF', '#8FB3FF', '#3D5AFE', '#0A2540'],
] as const;

export function orbBackground(index: number): string {
  const [a, b, c, d] = ORBS[((index % ORBS.length) + ORBS.length) % ORBS.length]!;
  return `radial-gradient(circle at 34% 28%, ${a} 0%, ${b} 24%, ${c} 62%, ${d} 100%)`;
}

/** A visitor as a soft gradient orb (the way a voice is shown), in its own colours. */
export function Orb({ index, size = 40, className, title }: { index: number; size?: number; className?: string; title?: string }) {
  return (
    <span
      className={className ? `orb ${className}` : 'orb'}
      style={{ width: size, height: size, background: orbBackground(index) }}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
    />
  );
}
